import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import * as telemarketingService from '../../services/crm/telemarketingService';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

const TrendingUpIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
    <polyline points="17 6 23 6 23 12"/>
  </svg>
);

const BarChartIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="20" x2="12" y2="10"/>
    <line x1="18" y1="20" x2="18" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="16"/>
  </svg>
);

const StarIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const DollarIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23"/>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);

export default function CampaignAnalytics() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  // @ts-ignore - organization_id exists on user object
  const activeOrgId = user?.organization_id || user?.user_metadata?.organization_id;
  const [campaign, setCampaign] = useState<telemarketingService.TelemarketingCampaign | null>(null);
  const [stats, setStats] = useState<telemarketingService.CampaignStats | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | 'all'>('7d');

  useEffect(() => {
    if (campaignId && activeOrgId) {
      loadData();
    }
  }, [campaignId, activeOrgId, timeRange]);

  // Standalone page - header managed inline

  const loadData = async () => {
    try {
      setLoading(true);
      const [campaignData, statsData, analyticsData] = await Promise.all([
        telemarketingService.getCampaign(campaignId!, activeOrgId!),
        telemarketingService.getCampaignStats(campaignId!),
        telemarketingService.getCampaignAnalytics(campaignId!)
      ]);
      
      setCampaign(campaignData);
      setStats(statsData);
      setAnalytics(analyticsData);
    } catch (error: any) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const conversionRate = stats.completed_calls > 0 
    ? ((stats.interested_count / stats.completed_calls) * 100).toFixed(1)
    : '0.0';

  const avgCallCost = stats.completed_calls > 0
    ? (stats.total_cost / stats.completed_calls).toFixed(2)
    : '0.00';

  const qualificationRate = stats.completed_calls > 0
    ? ((stats.qualified_leads / stats.completed_calls) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
      {/* Page Header */}
      {campaign && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Analytics: {campaign.name}</h1>
          <p className="text-gray-600 mt-1">Performance insights and conversion metrics</p>
        </div>
      )}

      {/* Time Range Selector */}
      <div className="flex justify-end">
        <div className="inline-flex rounded-lg border border-gray-300 bg-white">
          <button
            onClick={() => setTimeRange('7d')}
            className={`px-4 py-2 text-sm font-medium ${
              timeRange === '7d'
                ? 'bg-purple-600 text-white'
                : 'text-gray-700 hover:bg-gray-50'
            } rounded-l-lg`}
          >
            Last 7 Days
          </button>
          <button
            onClick={() => setTimeRange('30d')}
            className={`px-4 py-2 text-sm font-medium border-l border-gray-300 ${
              timeRange === '30d'
                ? 'bg-purple-600 text-white'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Last 30 Days
          </button>
          <button
            onClick={() => setTimeRange('all')}
            className={`px-4 py-2 text-sm font-medium border-l border-gray-300 ${
              timeRange === 'all'
                ? 'bg-purple-600 text-white'
                : 'text-gray-700 hover:bg-gray-50'
            } rounded-r-lg`}
          >
            All Time
          </button>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Conversion Rate</p>
              <p className="text-3xl font-bold text-gray-900">{conversionRate}%</p>
              <p className="text-sm text-green-600 mt-1">
                {stats.interested_count} interested
              </p>
            </div>
            <TrendingUpIcon className="w-12 h-12 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Qualified Leads</p>
              <p className="text-3xl font-bold text-gray-900">{stats.qualified_leads}</p>
              <p className="text-sm text-purple-600 mt-1">
                {qualificationRate}% qualification rate
              </p>
            </div>
            <StarIcon className="w-12 h-12 text-purple-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Meetings Booked</p>
              <p className="text-3xl font-bold text-gray-900">{stats.meetings_booked}</p>
              <p className="text-sm text-blue-600 mt-1">
                from {stats.completed_calls} calls
              </p>
            </div>
            <BarChartIcon className="w-12 h-12 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Avg Call Cost</p>
              <p className="text-3xl font-bold text-gray-900">${avgCallCost}</p>
              <p className="text-sm text-gray-600 mt-1">
                ${stats.total_cost.toFixed(2)} total
              </p>
            </div>
            <DollarIcon className="w-12 h-12 text-gray-600" />
          </div>
        </div>
      </div>

      {/* Call Outcomes Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Call Outcomes</h3>
        <div className="space-y-3">
          {[
            { label: 'Interested', count: stats.interested_count, color: 'bg-green-600' },
            { label: 'Not Interested', count: stats.completed_calls - stats.interested_count, color: 'bg-red-600' },
            { label: 'Meeting Booked', count: stats.meetings_booked, color: 'bg-purple-600' },
            { label: 'Qualified', count: stats.qualified_leads, color: 'bg-blue-600' }
          ].map((outcome) => {
            const percentage = stats.completed_calls > 0 
              ? (outcome.count / stats.completed_calls) * 100
              : 0;
            
            return (
              <div key={outcome.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{outcome.label}</span>
                  <span className="text-sm text-gray-500">
                    {outcome.count} ({percentage.toFixed(1)}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`${outcome.color} h-2 rounded-full transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sentiment Analysis */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Average Sentiment</h3>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <div>
                  <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-purple-600 bg-purple-200">
                    Sentiment Score
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-semibold inline-block text-purple-600">
                    {((stats.avg_sentiment + 1) * 50).toFixed(0)}%
                  </span>
                </div>
              </div>
              <div className="overflow-hidden h-4 mb-4 text-xs flex rounded-full bg-purple-100">
                <div
                  style={{ width: `${((stats.avg_sentiment + 1) * 50)}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 transition-all duration-500"
                />
              </div>
            </div>
          </div>
          <div className="ml-6 text-center">
            <div className="text-4xl font-bold text-gray-900">
              {stats.avg_sentiment > 0 ? '😊' : stats.avg_sentiment < -0.3 ? '😞' : '😐'}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {stats.avg_sentiment > 0.3 ? 'Positive' : stats.avg_sentiment < -0.3 ? 'Negative' : 'Neutral'}
            </div>
          </div>
        </div>
      </div>

      {/* ROI Calculator */}
      <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg shadow p-6 text-white">
        <h3 className="text-xl font-semibold mb-4">Campaign ROI</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-purple-100 text-sm mb-1">Total Investment</p>
            <p className="text-3xl font-bold">${stats.total_cost.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-purple-100 text-sm mb-1">Cost per Lead</p>
            <p className="text-3xl font-bold">
              ${stats.qualified_leads > 0 ? (stats.total_cost / stats.qualified_leads).toFixed(2) : '0.00'}
            </p>
          </div>
          <div>
            <p className="text-purple-100 text-sm mb-1">Cost per Meeting</p>
            <p className="text-3xl font-bold">
              ${stats.meetings_booked > 0 ? (stats.total_cost / stats.meetings_booked).toFixed(2) : '0.00'}
            </p>
          </div>
        </div>
        <div className="mt-4 p-4 bg-white/10 rounded-lg">
          <p className="text-sm text-purple-100">
            💡 <strong>Insight:</strong> {
              stats.meetings_booked > 0
                ? `At $${(stats.total_cost / stats.meetings_booked).toFixed(2)} per meeting, this campaign is ${
                    (stats.total_cost / stats.meetings_booked) < 50 ? 'highly efficient' : 'performing well'
                  }. Industry average is $75-150 per booked meeting.`
                : 'Continue calling to generate meetings and calculate ROI.'
            }
          </p>
        </div>
      </div>

      {/* Performance Tips */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">💡 Optimization Tips</h3>
        <div className="space-y-3">
          {stats.avg_sentiment < 0 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Low Sentiment Detected:</strong> Consider adjusting your script to be more empathetic and customer-focused.
              </p>
            </div>
          )}
          {stats.conversion_rate < 10 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Low Conversion:</strong> Try A/B testing different opening lines or adjust targeting criteria.
              </p>
            </div>
          )}
          {stats.qualified_leads > 10 && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>Great Lead Quality!</strong> Your campaign is generating high-quality leads. Consider scaling up.
              </p>
            </div>
          )}
          {stats.meetings_booked === 0 && stats.completed_calls > 20 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>No Meetings Booked:</strong> Review your call recordings and refine your value proposition.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-4">
        <button
          onClick={() => navigate(`/crm/telemarketing/${campaignId}/calls`)}
          className="px-6 py-3 bg-white border-2 border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 font-medium"
        >
          View Call History
        </button>
        <button
          onClick={() => navigate(`/crm/telemarketing/${campaignId}/edit`)}
          className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
        >
          Optimize Campaign
        </button>
      </div>
    </div>
  );
}
